import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import is from '@sindresorhus/is';

import type { SelectChangeEvent } from '@mui/material/Select';
import type { FocusEventHandler, MouseEventHandler, ReactElement, ReactNode } from 'react';

type OptionTypes = number | string;

type SelectOption<T> = {
    readonly key: number | string;
    readonly label: string;
    readonly value: T;
};

export type SelectProps<T extends OptionTypes = string> = {
    readonly disabled?: boolean;
    readonly helperText?: string | undefined;
    readonly isError?: boolean;
    readonly isFullWidth?: boolean;
    readonly isUnderlineDisabled?: boolean;
    readonly label?: string;
    readonly name: string;
    readonly onBlur?: FocusEventHandler;
    readonly onChange: (event: SelectChangeEvent<T>, child: ReactNode) => void;
    readonly onMouseLeave?: MouseEventHandler;
    readonly onMouseOver?: MouseEventHandler;
    readonly options: ReadonlyArray<SelectOption<T>>;
    readonly selectedValue?: SelectOption<T>['value'];
};

export const Select = <T extends OptionTypes>(props: SelectProps<T>): ReactElement<any> | null => {
    const {
        disabled,
        helperText,
        isError = false,
        isFullWidth = false,
        isUnderlineDisabled,
        label,
        name,
        onBlur,
        onChange,
        options,
        selectedValue = '',
    } = props;
    const optionalOnBlur = is.function(onBlur) === true ? { onBlur } : {};

    return (
        <FormControl aria-label="Select" error={isError} fullWidth={isFullWidth} variant="standard">
            {is.nonEmptyString(label) && <InputLabel>{label}</InputLabel>}
            <MuiSelect
                disabled={disabled === true}
                disableUnderline={isUnderlineDisabled ?? false}
                name={name}
                value={selectedValue}
                variant="standard"
                onChange={onChange}
                {...optionalOnBlur}
            >
                {options.map(({ key, label: optionLabel, value }) => (
                    <MenuItem key={key} aria-label="Menu Item" value={value}>
                        {optionLabel}
                    </MenuItem>
                ))}
            </MuiSelect>
            {is.nonEmptyString(helperText) && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
};

export type DecentSelectProps<T extends OptionTypes = string> = Omit<SelectProps<T>, 'label'> & {
    readonly label?: string | undefined;
};

export const DecentSelect = <T extends OptionTypes>(props: DecentSelectProps<T>): ReactNode => {
    const {
        disabled,
        isFullWidth,
        isUnderlineDisabled,
        label,
        onChange,
        onMouseLeave,
        onMouseOver,
        options,
        selectedValue,
    } = props;

    return (
        <FormControl disabled={disabled} fullWidth={isFullWidth ?? false} variant="standard">
            {is.nonEmptyString(label) && <InputLabel>{label}</InputLabel>}
            <MuiSelect
                disableUnderline={isUnderlineDisabled ?? false}
                fullWidth={isFullWidth ?? false}
                sx={{ border: 'none' }}
                value={selectedValue ?? ''}
                variant="standard"
                onChange={onChange}
                onMouseLeave={onMouseLeave}
                onMouseOver={onMouseOver}
            >
                {options.map(({ key, label, value }) => (
                    <MenuItem key={key} value={value}>
                        {label}
                    </MenuItem>
                ))}
            </MuiSelect>
        </FormControl>
    );
};
